// The Payouts page's reads and writes (spec 053). No whole-collection read (D17): tutors by
// role, one cycle's bookings by end time, one cycle's payouts by key, the history capped.
import { collection, getDocs, limit as qlimit, orderBy, query, Timestamp, where } from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { db, functions, storage } from "./firebase";

const rows = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));

// Every tutor, internal ones included — the page leaves them out (D11) and Owed is summed here (R10).
export const getTutorsForPay = async () =>
  rows(await getDocs(query(collection(db, "users"), where("role", "==", "teacher"))));

// One cycle's lessons: those that end inside it (047 D47's slice; R11).
export const getCycleBookings = async ({ start, end }) =>
  rows(
    await getDocs(
      query(
        collection(db, "bookings"),
        where("endTimeUTC", ">=", Timestamp.fromDate(start)),
        where("endTimeUTC", "<", Timestamp.fromDate(end))
      )
    )
  );

export const getCyclePayouts = async (cycleKey) =>
  rows(await getDocs(query(collection(db, "payouts"), where("cycleKey", "==", cycleKey))));

export const getPayoutHistory = async (max = 100) =>
  rows(await getDocs(query(collection(db, "payouts"), orderBy("createdAt", "desc"), qlimit(max))));

// Money → Overview (D104): the payouts recorded in a window — a single-field range, no order
// (checked on both projects by REST, 2026-09-30).
export const getPayoutsBetween = async ({ from, to }) =>
  rows(
    await getDocs(
      query(
        collection(db, "payouts"),
        where("createdAt", ">=", Timestamp.fromDate(from)),
        where("createdAt", "<", Timestamp.fromDate(to))
      )
    )
  );

// D4/D15: the receipt goes to payoutReceipts/{teacherId}/{random id}; recordPayout checks that path.
export const uploadPayoutReceipt = async (teacherId, file) => {
  const path = `payoutReceipts/${teacherId}/${crypto.randomUUID()}`;
  await uploadBytes(storageRef(storage, path), file, { contentType: file.type });
  return path;
};

export const receiptUrl = (path) => getDownloadURL(storageRef(storage, path));

// Opens a receipt in a new tab (R7). The tab opens first, inside the click, so a popup blocker
// that refuses window.open after an await (Safari) lets it through; the file's URL follows.
// Throws an Error whose message staff can read.
export const openReceipt = async (path) => {
  const tab = window.open("", "_blank");
  if (!tab) throw new Error("Allow pop-ups to open the receipt.");
  try {
    const url = await receiptUrl(path);
    tab.opener = null;
    tab.location.href = url;
  } catch (error) {
    tab.close();
    console.error("openReceipt failed:", error);
    throw new Error("Could not open the receipt.");
  }
};

// The callables. Errors are rethrown, not swallowed: "paid" must never show for a call that
// did not commit.
export const recordCyclePayout = async ({ teacherId, cycleKey, amount, receiptPath, receiptName, notes }) => {
  const { data } = await httpsCallable(functions, "recordPayout")({ teacherId, cycleKey, amount, receiptPath, receiptName, notes });
  return data;
};

export const undoPayout = async ({ payoutId, reason }) => {
  const { data } = await httpsCallable(functions, "undoPayout")({ payoutId, reason });
  return data;
};
